/* eslint-disable jsx-a11y/anchor-is-valid */
import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';
import React from 'react';
import { BadgeTW } from 'tailwindComponents/badgeTW/BadgeTW';
import { SimpleToggleTW } from 'tailwindComponents/toggleTW/SimpleToggleTW';
import { prettyInternalTransferCategory } from 'types/accounting/response/eInternalTransferCategory';
import { IDisburseAllDisbursementData } from 'types/order/escrow/IDisburseAllDisbursementData';
import {
  IDisburseAllResult,
  IDisburseResultInfo,
  eDisbursementResultStatus,
  mapDisbursementTypeToDisbursementResult,
} from 'types/order/escrow/IDisburseAllResult';
import { eDisbursementType } from 'types/order/escrow/eDisbursementType';
import { Currency } from 'ui-toolkit';
import { StyledDocumentIcon } from '../edit/lineItem/Fee.styles';
import { faFile } from '@fortawesome/pro-regular-svg-icons';
import { addressDataToString, IAddressData } from '../../../../ui-toolkit/orders/interfaces/IAddressResponse';

export const createDisbursementColumns = (
  showToggle: boolean,
  setSelectedDisbursement?: (x: IDisburseAllDisbursementData) => void,
  disburseResults?: IDisburseAllResult
) => {
  const columnHelper = createColumnHelper<IDisburseAllDisbursementData>();

  const getStatusBadge = (id: string, type: eDisbursementType) => {
    const resultKey: string = mapDisbursementTypeToDisbursementResult[type];
    const disburseResultSection: IDisburseResultInfo[] | undefined = disburseResults?.[resultKey];
    const disbursementResult: IDisburseResultInfo | undefined = disburseResultSection?.find(
      (x) => x.disbursementId === id
    );

    if (!disbursementResult) {
      return <BadgeTW className="bg-gray-100 text-gray-800">Not Sent</BadgeTW>;
    }
    return (
      <BadgeTW
        className={
          disbursementResult.status === eDisbursementResultStatus.Initiated
            ? 'bg-blue-100 capitalize text-blue-800'
            : 'bg-red-100 capitalize text-red-800'
        }
      >
        {disbursementResult.status}
      </BadgeTW>
    );
  };

  const disbursementIdColumn = columnHelper.accessor('disbursement.id', {
    size: 48,
    cell: ({ row: { original } }) => (
      <div className="flex items-center justify-center gap-1">
        <SimpleToggleTW key={original.disbursement.id} checked={original.enabled} onChange={original.onToggle} />{' '}
        {!!original.fee.linkedDocumentIds.length && <StyledDocumentIcon data-test-id={'document-icon'} icon={faFile} />}
      </div>
    ),
    header: '',
  });

  const disbursementStatusColumn = columnHelper.accessor('fee.statementFeeId', {
    size: 96,
    cell: ({ row: { original } }) => (
      <div className="flex w-24 justify-start">
        {getStatusBadge(original.disbursement.id, original.disbursement.type)}
      </div>
    ),
    header: 'Status',
  });

  const leadingColumns = disburseResults ? [disbursementStatusColumn] : [];

  if (showToggle) {
    leadingColumns.push(disbursementIdColumn);
  }

  const amountCentsColumn = columnHelper.accessor('disbursement.amountCents', {
    size: 128,
    cell: (info) => (
      <div className="flex justify-start">
        <Currency className="flex text-left" value={info.getValue()} valueIsCents={true} />
      </div>
    ),
    header: 'Amount',
  });

  const columnReducers: Record<eDisbursementType, ColumnDef<IDisburseAllDisbursementData, any>[]> = {
    [eDisbursementType.Check]: [
      ...leadingColumns,
      columnHelper.accessor('disbursement.name', {
        size: 128,
        header: 'Beneficiary',
        cell: (info) => <div className="flex w-32 justify-start">{info.getValue()}</div>,
      }),
      amountCentsColumn,
      columnHelper.accessor('disbursement.mailingAddress', {
        size: 128,
        header: 'Address',
        cell: (info: CellContext<IDisburseAllDisbursementData, IAddressData>) => (
          <span className="flex text-left">{addressDataToString(info.getValue())}</span>
        ),
      }),
      columnHelper.accessor('disbursement.memo', {
        size: 0,
        minSize: 0,
        header: 'Memo',
        cell: (info) => <span className="flex text-left">{info.getValue()}</span>,
      }),
    ],
    [eDisbursementType.ACH]: [
      ...leadingColumns,
      columnHelper.accessor('disbursement.account.name', {
        size: 128,
        header: 'Beneficiary',
        cell: (info) => <div className="flex w-32 justify-start">{info.getValue()}</div>,
      }),
      amountCentsColumn,
      columnHelper.accessor('disbursement.account.routingNumber', {
        size: 96,
        header: 'Routing #',
        cell: (info) => <span className="flex text-left">{info.getValue()}</span>,
      }),
      columnHelper.accessor('disbursement.escrowBankAccount.accountNumber', {
        size: 96,
        header: 'Account #',
        cell: (info) => <span className="flex text-left">{info.getValue()}</span>,
      }),
      columnHelper.accessor('disbursement.wellsFargoTransactionNumber', {
        size: 0,
        minSize: 0,
        header: 'Memo',
        cell: (info) => <span className="flex text-left">{info.getValue()}</span>,
      }),
    ],
    [eDisbursementType.Wire]: [
      ...leadingColumns,
      columnHelper.accessor('disbursement.beneficiary', {
        size: 128,
        header: 'Beneficiary',
        cell: (info) => <div className="flex w-32 justify-start">{info.getValue()}</div>,
      }),
      amountCentsColumn,
      columnHelper.accessor('disbursement.routingNumber', {
        size: 96,
        header: 'Routing #',
        cell: (info) => <span className="flex text-left">{info.getValue()}</span>,
      }),
      columnHelper.accessor('disbursement.escrowBankAccount.accountNumber', {
        size: 96,
        header: 'Account #',
        cell: (info) => <span className="flex text-left">{info.getValue()}</span>,
      }),
      columnHelper.accessor('disbursement.memo', {
        size: 0,
        minSize: 0,
        header: 'Memo',
        cell: (info) => <span className="flex text-left">{info.getValue()}</span>,
      }),
    ],
    [eDisbursementType.InternalTransfer]: [
      ...leadingColumns,
      amountCentsColumn,
      columnHelper.accessor('disbursement.category', {
        size: 128,
        header: 'Category',
        cell: ({ row: { original } }) => (
          <span className="flex text-left">{prettyInternalTransferCategory[original.disbursement.category ?? '']}</span>
        ),
      }),
      columnHelper.accessor('disbursement.destinationAccount.accountNumber', {
        size: 0,
        minSize: 0,
        header: 'Destination Account',
        cell: (info) => <span className="flex text-left">{info.getValue()}</span>,
      }),
    ],
    [eDisbursementType.Unknown]: [],
  };

  return columnReducers;
};
